import React, { useState, useEffect } from 'react';
import './VoiceWidgetPreview.css';

interface VoiceWidgetPreviewProps {
    voiceMode: any;
    style?: React.CSSProperties;
}

const VoiceWidgetPreview: React.FC<VoiceWidgetPreviewProps> = ({
    voiceMode,
    style = {}
}) => {
    const [isListening, setIsListening] = useState(false);
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    if (!voiceMode?.enabled) {
        return null;
    }

    const bannerTrigger = voiceMode.bannerTrigger;
    const scrollTrigger = voiceMode.scrollTrigger;

    // Show scroll trigger if banner exists and we've scrolled past threshold
    const showScrollTrigger = scrollTrigger &&
        bannerTrigger &&
        scrollY > (scrollTrigger.showAfterScroll || 200);

    const getVoiceIcon = (iconType: string) => {
        switch (iconType) {
            case 'microphone':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                );
            case 'voice':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5h4V8h2v4h4l-5 5z"/>
                    </svg>
                );
            case 'mic-circle':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2s-2-.9-2-2V8c0-1.1.9-2 2-2z"/>
                        <path d="M8 12h2v6h8v-6h2v6c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2v-6z"/>
                    </svg>
                );
            default:
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                );
        }
    };

    const getBannerTriggerStyles = () => {
        if (!bannerTrigger) return {};

        const baseStyles: React.CSSProperties = {
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: bannerTrigger.borderRadius || '50%',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            ...style
        };

        // Position
        switch (bannerTrigger.position) {
            case 'top-left':
                baseStyles.top = '20px';
                baseStyles.left = '20px';
                break;
            case 'top-center':
                baseStyles.top = '20px';
                baseStyles.left = '50%';
                baseStyles.transform = 'translateX(-50%)';
                break;
            case 'top-right':
                baseStyles.top = '20px';
                baseStyles.right = '20px';
                break;
            case 'bottom-left':
                baseStyles.bottom = '20px';
                baseStyles.left = '20px';
                break;
            case 'bottom-center':
                baseStyles.bottom = '20px';
                baseStyles.left = '50%';
                baseStyles.transform = 'translateX(-50%)';
                break;
            case 'bottom-right':
                baseStyles.bottom = '20px';
                baseStyles.right = '20px';
                break;
        }

        // Size
        const sizeMap: Record<string, { width: string; height: string }> = {
            small: { width: '40px', height: '40px' },
            medium: { width: '50px', height: '50px' },
            large: { width: '60px', height: '60px' }
        };
        const size = sizeMap[bannerTrigger.size || 'medium'] || sizeMap.medium;

        return {
            ...baseStyles,
            ...size,
            backgroundColor: bannerTrigger.bgColor || '#007bff',
            color: bannerTrigger.iconColor || '#ffffff',
            border: bannerTrigger.borderWidth ? `${bannerTrigger.borderWidth} solid ${bannerTrigger.borderColor}` : 'none',
            boxShadow: bannerTrigger.boxShadow || '0 2px 10px rgba(0,0,0,0.2)',
            animation: bannerTrigger.pulseAnimation ? 'voice-pulse 2s infinite' : 'none'
        };
    };

    const getScrollTriggerStyles = () => {
        if (!scrollTrigger || !showScrollTrigger) return { display: 'none' };

        const baseStyles: React.CSSProperties = {
            position: 'fixed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: scrollTrigger.borderRadius || '50%',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            ...style
        };

        // Position
        switch (scrollTrigger.position) {
            case 'bottom-right':
                baseStyles.bottom = '20px';
                baseStyles.right = '20px';
                break;
            case 'bottom-left':
                baseStyles.bottom = '20px';
                baseStyles.left = '20px';
                break;
            case 'middle-right':
                baseStyles.top = '50%';
                baseStyles.right = '20px';
                baseStyles.transform = 'translateY(-50%)';
                break;
            case 'middle-left':
                baseStyles.top = '50%';
                baseStyles.left = '20px';
                baseStyles.transform = 'translateY(-50%)';
                break;
        }

        // Size
        const sizeMap: Record<string, { width: string; height: string }> = {
            small: { width: '40px', height: '40px' },
            medium: { width: '50px', height: '50px' },
            large: { width: '60px', height: '60px' }
        };
        const size = sizeMap[scrollTrigger.size || 'medium'] || sizeMap.medium;

        return {
            ...baseStyles,
            ...size,
            backgroundColor: scrollTrigger.bgColor || '#007bff',
            color: scrollTrigger.iconColor || '#ffffff',
            border: scrollTrigger.borderWidth ? `${scrollTrigger.borderWidth} solid ${scrollTrigger.borderColor}` : 'none',
            boxShadow: scrollTrigger.boxShadow || '0 2px 10px rgba(0,0,0,0.2)',
            animation: scrollTrigger.pulseAnimation ? 'voice-pulse 2s infinite' : 'none'
        };
    };

    const handleVoiceClick = () => {
        setIsListening(!isListening);
        // Simulate voice interaction
        setTimeout(() => {
            setIsListening(false);
        }, 2000);
    };

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: 300,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {/* Simulated Website Content */}
            <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                textAlign: 'center',
                padding: 40
            }}>
                <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
                    🛍️ Your E-Commerce Store
                </div>
                <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 16 }}>
                    Try clicking the voice button to search!
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 12,
                    width: '100%',
                    maxWidth: 400
                }}>
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} style={{
                            background: 'rgba(255,255,255,0.15)',
                            borderRadius: 8,
                            padding: 16,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <div style={{ fontSize: 32, marginBottom: 4 }}>
                                {i === 1 ? '🍕' : i === 2 ? '🍔' : i === 3 ? '🍜' : '🥗'}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.9 }}>Product {i}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Banner Voice Trigger */}
            {bannerTrigger && (
                <div
                    className={`voice-banner-trigger ${isListening ? 'listening' : ''}`}
                    style={getBannerTriggerStyles()}
                    onClick={handleVoiceClick}
                    title={bannerTrigger.tooltipText || 'Click to start voice search'}
                >
                    {getVoiceIcon(bannerTrigger.icon)}
                    {bannerTrigger.showText && bannerTrigger.text && (
                        <span style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            color: bannerTrigger.textColor || '#ffffff'
                        }}>
                            {bannerTrigger.text}
                        </span>
                    )}
                    {isListening && (
                        <div className="voice-listening-indicator">
                            <div className="listening-pulse"></div>
                            <span>Listening...</span>
                        </div>
                    )}
                </div>
            )}

            {/* Scroll Voice Trigger - only show in preview if enabled */}
            {showScrollTrigger && (
                <div
                    className={`voice-scroll-trigger ${isListening ? 'listening' : ''}`}
                    style={getScrollTriggerStyles()}
                    onClick={handleVoiceClick}
                    title={scrollTrigger?.tooltipText || 'Click to start voice search'}
                >
                    {getVoiceIcon(scrollTrigger?.icon || 'microphone')}
                    {isListening && (
                        <div className="voice-listening-indicator">
                            <div className="listening-pulse"></div>
                            <span>Listening...</span>
                        </div>
                    )}
                </div>
            )}

        </div>
    );
};

export default VoiceWidgetPreview;
